import { useEffect, useRef, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../auth/AuthContext";
import UserService from "../../services/UserService";
import AlertService from "../../services/AlertService";
import Fqicon from "../../assets/img/Fq.png";

const MFAVerify = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const [otp, setOtp] = useState(Array(6).fill(""));
  const inputRefs = useRef([]);

  const [loadingVerify, setLoadingVerify] = useState(false);
  const [loadingSendCode, setLoadingSendCode] = useState(false);

  useEffect(() => {
    if (authContext?.mfaVerified) {
      navigate("/");
    }
  }, [authContext?.mfaVerified, navigate]);

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(paste)) return;

    const newOtp = paste.split("");
    setOtp([...newOtp, ...Array(6 - newOtp.length).fill("")]);
    inputRefs.current[Math.min(paste.length - 1, 5)]?.focus();
  };


  const handleVerify = async (e) => {
    e.preventDefault();
    const finalOtp = otp.join("");

    if (finalOtp.length < 6) {
      AlertService.error("Please enter the 6 digit OTP");
      return;
    }

    try {
      setLoadingVerify(true);
      const result = await UserService.VerifyQrCode(finalOtp, authContext);

      if (result.isSuccess) {
        authContext.setMFAVerified(true);
        navigate("/");
      } else {
        AlertService.error(result.errorMessage);
      }
    } catch (err) {
      console.error(err);
      AlertService.error("OTP verification failed");
    } finally {
      setLoadingVerify(false);
    }
  };

  const handleSendCode = async () => {
    try {
      setLoadingSendCode(true);
      const result = await UserService.SendOTP(authContext);

      if (result.isSuccess) {
        AlertService.success(result.message);
             navigate("/email-verify-otp");
      } else {
        AlertService.error(result.errorMessage);
      }
    } catch (err) {
      console.error(err);
      AlertService.error("Failed to send OTP");
    } finally {
      setLoadingSendCode(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex font-sans">

        {/* LEFT PANEL */}
        <div className="hidden md:flex w-1/2 bg-[#0e3a46] relative overflow-hidden">
          <div className="absolute top-12 -right-10 bg-white h-24 w-56 rounded-full flex items-center pl-12 shadow-md">
            <img src={Fqicon} alt="Logo" className="h-12" />
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="flex w-full md:w-1/2 items-center justify-center bg-white px-4">
          <form
            onSubmit={handleVerify}
            onPaste={handlePaste}
            className="w-full max-w-[420px] text-center"
          >
            <h2 className="text-2xl font-semibold mb-3">
              Two Factor Verification
            </h2>

            <p className="text-base text-gray-600 mb-6 leading-relaxed">
              Please provide the One-Time Password generated by Microsoft
              Authenticator or any Authenticator app.
            </p>

            {/* OTP INPUTS */}
            <div className="flex justify-center  gap-3 xl:gap-5 mb-6">
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  value={digit}
                  maxLength={1}
                  inputMode="numeric"
                  onChange={(e) => handleChange(e.target.value, i)}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  onFocus={(e) => e.target.select()}
                  autoFocus={i === 0} 
                  className="
                    w-12 h-12
                    border border-gray-300
                    rounded-md
                    text-center text-lg
                    outline-none
                    focus:ring-2 focus:ring-[#0e3a46]
                  "
                />
              ))}
            </div>

            {/* VERIFY */}
            <button
              type="submit"
              disabled={loadingVerify}
              className={`
                w-full h-11 rounded-md
                text-white font-medium
                bg-[#0e3a46] hover:opacity-95 mb-4
                ${loadingVerify && "opacity-50 cursor-not-allowed"}
              `}
            >
              {loadingVerify ? "Verifying..." : "Verify"}
            </button>

            <p className="text-sm text-gray-600 mb-4">
              I can’t use my authenticator app right now
            </p>

            {/* SEND OTP */}
            <button
              type="button"
              onClick={handleSendCode}
              disabled={loadingSendCode}
              className={`
                w-full h-11
                border border-gray-300
                rounded-md
                text-base font-medium
                hover:bg-teal-50
                ${loadingSendCode && "opacity-50 cursor-not-allowed"}
              `}
            >
              {loadingSendCode ? "Sending..." : "Send Code via Email"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default MFAVerify;